import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  InputAdornment,
  Avatar,
  Link,
  MenuItem,
  Grid2 as Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Handshake as HandshakeIcon,
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { cmsService, CMSParceiro } from '../../services';

const CMSParceiros: React.FC = () => {
  const { t } = useTranslation();
  const [parceiros, setParceiros] = useState<CMSParceiro[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState<Partial<CMSParceiro> | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; item: CMSParceiro | null }>({
    open: false,
    item: null,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cmsService.getParceiros();
      setParceiros(res.data.data || []);
    } catch (err: any) {
      setError(err.message || t('cms.errorLoadingPartners'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenEdit = (parceiro?: CMSParceiro) => {
    if (parceiro) {
      setEditData(parceiro);
    } else {
      setEditData({
        nome: '',
        logo_url: '',
        website_url: '',
        descricao: '',
        ordem: 0,
        status: 'ativo',
      });
    }
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editData?.nome || !editData?.logo_url) {
      setError(t('cms.partnerNameLogoRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const parceiroData = {
        nome: editData.nome,
        logo_url: editData.logo_url,
        website_url: editData.website_url || '',
        descricao: editData.descricao || '',
        ordem: editData.ordem || 0,
        status: editData.status || 'ativo',
      };

      if (editData.id) {
        await cmsService.updateParceiro(editData.id, parceiroData);
        setSuccess(t('cms.partnerUpdated'));
      } else {
        await cmsService.createParceiro(parceiroData);
        setSuccess(t('cms.partnerCreated'));
      }
      setEditDialog(false);
      setEditData(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingPartner'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploading(true);
      setError(null);
      try {
        const res = await cmsService.uploadMediaBase64(file, 'parceiros', editData?.nome || file.name);
        const url = res.data.data?.url;
        if (url) {
          setEditData((prev) => ({ ...prev, logo_url: url }));
        } else {
          setError(t('cms.errorSavingPartner'));
        }
      } catch (err: any) {
        setError(err.message || t('cms.errorSavingPartner'));
      } finally {
        setUploading(false);
      }
    }
    // permitir re-selecionar o mesmo ficheiro
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async () => {
    if (!deleteDialog.item) return;
    try {
      await cmsService.deleteParceiro(deleteDialog.item.id);
      setSuccess(t('cms.partnerDeleted'));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorDeletingPartner'));
    }
  };

  const filteredParceiros = parceiros.filter((p) => {
    if (!searchTerm) return true;
    return (
      p.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.website_url?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('cms.partnersTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cms.partnersSubtitle')}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          {t('cms.newPartner')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('cms.searchPartners')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('common.total')}</Typography>
              <Typography variant="h4" fontWeight="bold">{parceiros.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('cms.activeCount')}</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {parceiros.filter((p) => p.status === 'ativo').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('cms.partner')}</TableCell>
                <TableCell>{t('cms.website')}</TableCell>
                <TableCell>{t('cms.order')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredParceiros.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <HandshakeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      {t('cms.noPartnerFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredParceiros.map((parceiro) => (
                  <TableRow key={parceiro.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          src={parceiro.logo_url}
                          variant="rounded"
                          sx={{ width: 56, height: 40, bgcolor: 'action.hover', '& img': { objectFit: 'contain' } }}
                        >
                          {parceiro.nome.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {parceiro.nome}
                          </Typography>
                          {parceiro.descricao && (
                            <Typography variant="caption" color="text.secondary">
                              {parceiro.descricao}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {parceiro.website_url ? (
                        <Link
                          href={parceiro.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {parceiro.website_url}
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>{parceiro.ordem}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={parceiro.status === 'ativo' ? 'success' : 'default'}
                        label={parceiro.status === 'ativo' ? t('common.active') : t('common.inactive')}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('common.edit')}>
                        <IconButton size="small" onClick={() => handleOpenEdit(parceiro)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, item: parceiro })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editData?.id ? t('cms.editPartner') : t('cms.newPartner')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('cms.partnerName')}
                value={editData?.nome || ''}
                onChange={(e) => setEditData((prev) => ({ ...prev, nome: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('cms.website')}
                value={editData?.website_url || ''}
                onChange={(e) => setEditData((prev) => ({ ...prev, website_url: e.target.value }))}
                placeholder="https://..."
              />
            </Grid>
            <Grid size={12}>
              <Box display="flex" gap={1} alignItems="flex-start">
                <TextField
                  fullWidth
                  label={t('cms.logoUrl')}
                  value={editData?.logo_url || ''}
                  onChange={(e) => setEditData((prev) => ({ ...prev, logo_url: e.target.value }))}
                  required
                  placeholder="https://..."
                  helperText={t('cms.logoUrlHelper')}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleLogoUpload}
                />
                <Button
                  variant="outlined"
                  startIcon={uploading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  sx={{ mt: 1, whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {uploading ? t('common.saving') : t('cms.uploadLogo')}
                </Button>
              </Box>
            </Grid>
            {editData?.logo_url && (
              <Grid size={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2, border: '1px dashed', borderColor: 'divider' }}
                >
                  <Box
                    component="img"
                    src={editData.logo_url}
                    alt={editData.nome || 'logo'}
                    sx={{ maxHeight: 60, maxWidth: '100%', objectFit: 'contain' }}
                  />
                </Box>
              </Grid>
            )}
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('cms.partnerDescription')}
                value={editData?.descricao || ''}
                onChange={(e) => setEditData((prev) => ({ ...prev, descricao: e.target.value }))}
                placeholder={t('cms.partnerDescriptionPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label={t('cms.order')}
                value={editData?.ordem ?? 0}
                onChange={(e) => setEditData((prev) => ({ ...prev, ordem: parseInt(e.target.value) || 0 }))}
                helperText={t('cms.orderHelperText')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={editData?.status || 'ativo'}
                onChange={(e) => setEditData((prev) => ({ ...prev, status: e.target.value as 'ativo' | 'inativo' }))}
              >
                <MenuItem value="ativo">{t('common.active')}</MenuItem>
                <MenuItem value="inativo">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !editData?.nome || !editData?.logo_url}
          >
            {saving ? t('common.saving') : editData?.id ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })}>
        <DialogTitle>{t('cms.deletePartner')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cms.confirmDeletePartner', { name: deleteDialog.item?.nome })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CMSParceiros;
